/**
 * HVP Cascade Controller - health checks
 *
 * Periodic self-repair of stuck readonly/disabled/empty dropdown states.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeHealthMethods = {
    /**
     * Start periodic health checks
     *
     * Runs every 5 seconds to detect and fix stuck dropdown states.
     */
    startHealthChecks() {
        if (this.healthCheckInterval) {
            return; // Already running
        }

        this.healthCheckInterval = setInterval(() => {
            this.runHealthCheck();
        }, 5000); // Every 5 seconds

        this.log('[HealthCheck] Started');
    },

    /**
     * Stop health checks
     */
    stopHealthChecks() {
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
            this.log('[HealthCheck] Stopped');
        }
    },

    /**
     * Run a health check on all dropdowns
     *
     * Detects and fixes:
     * - Stuck readonly states
     * - Incorrectly disabled dropdowns
     * - Missing options
     */
    runHealthCheck() {
        let fixedCount = 0;

        this.dropdowns.forEach((select, attrName) => {
            // Check 1: Single-option shouldn't have readonly class if originally multi-option
            const isSingleOption = select.classList.contains('hvp-single-option');
            const optionCount = Array.from(select.options).filter(opt => opt.value !== '').length;

            if (isSingleOption && optionCount > 1) {
                // This was marked as single-option but now has multiple options
                select.classList.remove('hvp-single-option');
                select.disabled = false;
                fixedCount++;
                this.log(`[HealthCheck] Fixed stuck readonly: ${attrName}`);
            }

            // Check 2: Interactive dropdowns shouldn't be disabled if they have options
            if (!isSingleOption && select.disabled && optionCount > 0 && !select.classList.contains('hvp-empty')) {
                select.disabled = false;
                fixedCount++;
                this.log(`[HealthCheck] Re-enabled dropdown: ${attrName}`);
            }

            // Check 3: Empty class should match actual state
            const hasOptions = optionCount > 0;
            const hasEmptyClass = select.classList.contains('hvp-empty');

            if (hasOptions && hasEmptyClass) {
                select.classList.remove('hvp-empty');
                fixedCount++;
                this.log(`[HealthCheck] Removed incorrect empty class: ${attrName}`);
            }
        });

        if (fixedCount > 0) {
            this.log(`[HealthCheck] Fixed ${fixedCount} issue(s)`);

            // Trigger UI update after fixes
            this.scheduler.scheduleUIUpdate('highlight', 'health-check');
        }
    },
};

export default CascadeHealthMethods;
